import React, { useContext, useEffect, useMemo, useState } from 'react';

import moment from 'moment';

import getDateRangePeriods from '../api/getDateRangePeriods';
import PeriodsContext from '../contexts/PeriodsContext';
import GqlClientContext from '../contexts/GqlClientContext';
import useDateScope from '../hooks/useDateScope';
import { DateTime } from 'luxon';

export const DEFAULT_CUSTOMER_LANE_PERIODS_DATE_RANGE: AdvancedRelativeDateRange =
  {
    from: { type: 'last', interval: 'week', n: 13, currentToDate: false },
    to: { type: 'next', interval: 'week', n: 13, currentToDate: false },
    config: { from: 'start', to: 'end' },
  };

const CustomerLaneBoardPeriodsProvider = ({
  children,
  board,
}: {
  children: JSX.Element | JSX.Element[];
  board: CustomerLaneBoard;
}) => {
  const { client } = useContext(GqlClientContext);
  const dateScope = useDateScope({});
  const [periods, setPeriods] = useState<Period[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState<Period | undefined>();

  useEffect(() => {
    let isActive = true;
    getDateRangePeriods({
      dateScope,
      dataType: board.dataType,
      client,
      cadence: 'week',
      cadenceInterval: 1,
    }).then((newPeriods) => {
      if (!isActive) {
        return;
      }
      const sorted = [...newPeriods]
        .sort((a, b) => {
          if (a.startDate < b.startDate) {
            return 1;
          } else if (a.startDate === b.startDate) {
            return 0;
          } else {
            return -1;
          }
        })
        .map((p) => ({
          ...p,
          label: `${moment.utc(p.startDate).format('MMM DD')} - ${moment.utc(p.endDate).format("MMM DD 'YY")}`,
        }));
      setPeriods(sorted);
    });
    return () => {
      isActive = false;
    };
  }, [board.dataType, client, dateScope]);

  useEffect(() => {
    if (periods.length === 0) {
      return;
    }
    setSelectedPeriod((current) => {
      if (current) {
        return current;
      }
      const today = DateTime.utc().toISODate();
      return (
        periods.find((p) => p.startDate <= today && today <= p.endDate) ??
        periods[0]
      );
    });
  }, [periods]);

  const value = useMemo(
    () => ({ periods, selectedPeriod, setSelectedPeriod }),
    [periods, selectedPeriod],
  );

  return (
    <PeriodsContext.Provider value={value}>{children}</PeriodsContext.Provider>
  );
};

export default CustomerLaneBoardPeriodsProvider;
